import { EnemyFactory } from "./data/enemyFactory";
import { SKILLS, SkillId } from "./data/skills";
import { EncounterEnemyConfig, MAX_ENCOUNTER_ENEMIES } from "./state/encounterEnemies";
import { EnemyId } from "./state/runState";
import { getAutomationOverride } from "./automation/automationTags";

export type DebugCombatOptions = {
  enemyConfig: EncounterEnemyConfig;
  playerSkills?: SkillId[];
  floor?: number;
};

const isDebugCombatEnabled = (params: URLSearchParams) => {
  const flag = params.get("debugCombat") ?? params.get("debug") ?? getAutomationOverride("combat");
  if (!flag) {
    return false;
  }

  const normalized = String(flag).toLowerCase();
  return normalized === "combat" || normalized === "1" || normalized === "true";
};

const isValidEnemyId = (id: unknown): id is EnemyId => {
  if (typeof id !== "string") {
    return false;
  }
  try {
    EnemyFactory.getDef(id as EnemyId);
    return true;
  } catch {
    return false;
  }
};

const parseEnemyConfigJson = (raw: string | null): EncounterEnemyConfig | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !Array.isArray((parsed as { enemies?: unknown }).enemies)
    ) {
      return undefined;
    }

    const enemies = (parsed as { enemies: unknown[] }).enemies
      .filter(
        (enemy): enemy is Record<string, unknown> => Boolean(enemy) && typeof enemy === "object"
      )
      .map((enemy) => {
        const id = (enemy as { id?: unknown }).id;
        if (!isValidEnemyId(id)) {
          return null;
        }

        const nameSuffix =
          typeof (enemy as { nameSuffix?: unknown }).nameSuffix === "string"
            ? (enemy as { nameSuffix?: string }).nameSuffix
            : undefined;

        return { id, nameSuffix };
      })
      .filter((enemy): enemy is { id: EnemyId; nameSuffix: string | undefined } => enemy !== null)
      .slice(0, MAX_ENCOUNTER_ENEMIES);

    return enemies.length > 0 ? { enemies } : undefined;
  } catch {
    return undefined;
  }
};

export const getDebugCombatOptions = (): DebugCombatOptions | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  if (!isDebugCombatEnabled(params)) {
    return null;
  }

  const enemyConfigOverride = getAutomationOverride<EncounterEnemyConfig | string>("enemyConfig");
  const enemyConfig = parseEnemyConfigJson(
    params.get("enemyConfig") ||
      (typeof enemyConfigOverride === "string"
        ? enemyConfigOverride
        : JSON.stringify(enemyConfigOverride))
  ) ?? {
    enemies: [{ id: "goblin" }],
  };

  if (!enemyConfig) {
    return null;
  }

  // Optional: enable specific player skills for debug via comma-separated list
  const skillsRaw = params.get("debugSkills") || getAutomationOverride<string>("skills");
  const playerSkills = skillsRaw
    ? (typeof skillsRaw === "string" ? skillsRaw.split(",") : (skillsRaw as string[]))
        .map((s) => s.trim())
        .filter((s): s is SkillId => s in SKILLS)
    : undefined;

  const floorRaw = params.get("debugFloor") || getAutomationOverride<string | number>("floor");
  const floor = floorRaw ? parseInt(String(floorRaw), 10) : undefined;

  return { enemyConfig, playerSkills, floor };
};
